import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { toggleMobileMenu, closeMobileMenu } from '../../store/slices/uiSlice';
import { useAuth } from '../../context/AuthContext';

const scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 100);
};


const Header = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { isMobileMenuOpen } = useSelector((state) => state.ui);
  const [isScrolled, setIsScrolled] = useState(false);
  const { role, logout } = useAuth();
  const isAdmin = role === 'admin';

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(closeMobileMenu());
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    dispatch(closeMobileMenu());
    // No automatic scroll to top on every navigation
  }, [location, dispatch]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Services', href: '/services' },
    { name: 'POSH Professionals', href: '/posh-professionals' },
    { name: 'For Clients', href: '/for-clients' },
    { name: 'For Candidates', href: '/for-candidates' },
    { name: 'Careers', href: '/careers' },
    { name: 'Blog', href: '/blog' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">

          <Link to="/" onClick={scrollToTop} className="flex items-center">
            <img
              src="/Genexa.png"
              alt="Genexasource Logo"
              className={`transition-all duration-300 h-16 sm:h-20 object-contain ${
                isScrolled ? 'w-28 sm:w-32' : 'w-32 sm:w-36'
              }`}
            />
          </Link>

          <nav className="hidden lg:flex items-center space-x-6">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={scrollToTop}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === item.href
                    ? 'text-primary-600'
                    : 'text-navy-700 hover:text-primary-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center space-x-4">
            {isAdmin ? (
              <>
                <Link
                  to="/admin"
                  onClick={scrollToTop}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="bg-red-50 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-100 transition-colors duration-200 text-sm"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/contact"
                onClick={scrollToTop}
                className="bg-primary-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200"
              >
                Get Started
              </Link>
            )}
          </div>

          <button
            onClick={() => dispatch(toggleMobileMenu())}
            className="lg:hidden p-2 rounded-md text-navy-700 hover:text-primary-600 hover:bg-primary-50 transition"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-6 space-y-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => {
                    scrollToTop();
                    dispatch(closeMobileMenu());
                  }}
                  className={`block text-base font-medium text-center py-2 ${
                    location.pathname === item.href
                      ? 'text-primary-600 bg-primary-50 rounded-lg'
                      : 'text-navy-700 hover:text-primary-600 hover:bg-gray-50 rounded-lg'
                  }`}
                >
                  {item.name}
                </Link>
              ))}

              <div className="pt-4 border-t border-gray-200 space-y-3">
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => {
                      scrollToTop();
                      dispatch(closeMobileMenu());
                    }}
                    className="block w-full bg-blue-50 text-blue-600 px-6 py-3 rounded-lg font-medium text-center hover:bg-blue-100 transition"
                  >
                    Admin Dashboard
                  </Link>
                )}
                <Link
                  to="/contact"
                  onClick={() => {
                    scrollToTop();
                    dispatch(closeMobileMenu());
                  }}
                  className="block w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-medium text-center hover:bg-primary-700 transition"
                >
                  Get Started
                </Link>
                {isAdmin && (
                  <button
                    onClick={handleLogout}
                    className="block w-full bg-red-50 text-red-600 px-6 py-3 rounded-lg font-medium text-center hover:bg-red-100 transition"
                  >
                    Logout
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
